import type { AdminHomeDropdownOption } from "./adminHomeDropdownStyles";

export type AdminHomeBasedOnValue = "select" | "region" | "revenue_stream";

export type AdminHomeFilterOptionGroup = {
  label: string;
  value: string;
  options: AdminHomeDropdownOption[];
};

export const BASED_ON_OPTIONS: AdminHomeDropdownOption[] = [
  { label: "Select", value: "select" },
  { label: "Region", value: "region" },
  { label: "Revenue Stream", value: "revenue_stream" },
];

export const DEFAULT_BASED_ON_OPTION: AdminHomeDropdownOption =
  BASED_ON_OPTIONS.find((option) => option.value === "revenue_stream") ??
  BASED_ON_OPTIONS[0];

export const getPartnerEcosystemDimensionLabel = (
  basedOnValue: string | null | undefined
): string => {
  if (basedOnValue === "region") {
    return "Region";
  }

  if (basedOnValue === "revenue_stream") {
    return "Revenue Stream";
  }

  return "Revenue Stream";
};

export const REGION_FILTER_VALUES = [
  "NAM",
  "LAM",
  "EMEA",
  "APAC",
  "Global",
  "Internal",
] as const;

/** Number of years before the current year to list in the dropdown. */
export const PREVIOUS_YEARS_LIST_COUNT = 6;

/** Current calendar year plus the previous six years (7 total). */
export const getListedYearFilterYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  return Array.from(
    { length: PREVIOUS_YEARS_LIST_COUNT + 1 },
    (_, index) => currentYear - index
  );
};

export const createYearFilterOption = (year: number): AdminHomeDropdownOption => ({
  label: `Year : ${year}`,
  value: `year-${year}`,
});

export const buildYearFilterOptions = (): AdminHomeDropdownOption[] =>
  getListedYearFilterYears().map((year) => createYearFilterOption(year));

export const YEAR_INPUT_OPTION_VALUE = "__admin_home_year_input__";

export const parseCustomYearInput = (value: string): number | null => {
  const trimmed = value.trim();

  if (!/^\d{4}$/.test(trimmed)) {
    return null;
  }

  const year = Number(trimmed);

  if (year < 1900 || year > 9999) {
    return null;
  }

  return year;
};

export const buildRegionDimensionOptions = (): AdminHomeDropdownOption[] => [
  { label: "Region : All", value: "region-all" },
  ...REGION_FILTER_VALUES.map((region) => ({
    label: `Region : ${region}`,
    value: `region-${region}`,
  })),
];

export const buildRevenueStreamDimensionOptions = (
  revenueStreams: { value: string }[] = []
): AdminHomeDropdownOption[] => [
  { label: "Revenue Stream : All", value: "revenue_stream-all" },
  ...revenueStreams.map((stream) => ({
    label: `Revenue Stream : ${stream.value}`,
    value: `revenue_stream-${stream.value}`,
  })),
];

export const buildGroupedFilterOptions = (
  basedOnValue: string | null | undefined,
  revenueStreams: { value: string }[] = []
): AdminHomeFilterOptionGroup[] => {
  if (!basedOnValue || basedOnValue === "select") {
    return [];
  }

  const yearGroup: AdminHomeFilterOptionGroup = {
    label: "Year",
    value: "year-group",
    options: buildYearFilterOptions(),
  };

  if (basedOnValue === "region") {
    return [
      {
        label: "Region",
        value: "region-group",
        options: buildRegionDimensionOptions(),
      },
      yearGroup,
    ];
  }

  if (basedOnValue === "revenue_stream") {
    return [
      {
        label: "Revenue Stream",
        value: "revenue-stream-group",
        options: buildRevenueStreamDimensionOptions(revenueStreams),
      },
      yearGroup,
    ];
  }

  return [yearGroup];
};

const isDimensionOption = (
  option: AdminHomeDropdownOption,
  basedOnValue: string | null | undefined
): boolean => {
  if (basedOnValue === "region") {
    return option.value.startsWith("region-");
  }

  if (basedOnValue === "revenue_stream") {
    return option.value.startsWith("revenue_stream-");
  }

  return false;
};

/** Keeps at most one dimension chip and one year chip in the combined filter dropdown. */
export const normalizeFilterSelection = (
  selected: AdminHomeDropdownOption[],
  basedOnValue: string | null | undefined
): AdminHomeDropdownOption[] => {
  let dimension: AdminHomeDropdownOption | undefined;
  let year: AdminHomeDropdownOption | undefined;

  for (const option of selected) {
    if (option.value === YEAR_INPUT_OPTION_VALUE) {
      continue;
    }

    if (option.value.startsWith("year-")) {
      year = option;
      continue;
    }

    if (isDimensionOption(option, basedOnValue)) {
      dimension = option;
    }
  }

  return [dimension, year].filter(
    (option): option is AdminHomeDropdownOption => Boolean(option)
  );
};

export const isFilterDropdownEnabled = (
  basedOnValue: string | null | undefined
): boolean => Boolean(basedOnValue && basedOnValue !== "select");

export const applyCustomYearToFilters = (
  filters: AdminHomeDropdownOption[],
  basedOnValue: string | null | undefined,
  year: number
): AdminHomeDropdownOption[] =>
  normalizeFilterSelection(
    [
      ...filters.filter((option) => !option.value.startsWith("year-")),
      createYearFilterOption(year),
    ],
    basedOnValue
  );
